import type { EndpointMeta } from "@/apis/types";
import {
  createFetchAndHook,
  type FetchFactory,
  type HookFactory,
} from "@/shared/factories";
import { wsfTerminalsApiMeta } from "../apiMeta";
import {
  type TerminalTransportsByTerminalIdInput,
  terminalTransportsByTerminalIdInputSchema,
} from "./shared/terminalTransports.input";
import {
  type TerminalTransport,
  terminalTransportSchema,
} from "./shared/terminalTransports.output";

/**
 * Metadata for the fetchTerminalTransportsByTerminalId endpoint
 */
export const terminalTransportsByTerminalIdMeta = {
  functionName: "fetchTerminalTransportsByTerminalId",
  endpoint: "/terminalTransports/{TerminalID}",
  inputSchema: terminalTransportsByTerminalIdInputSchema,
  outputSchema: terminalTransportSchema,
  sampleParams: { TerminalID: 10 },
  endpointDescription:
    "Get transportation information for a specific terminal by ID.",
} satisfies EndpointMeta<
  TerminalTransportsByTerminalIdInput,
  TerminalTransport
>;

/**
 * Fetch function for retrieving transportation information for a specific terminal by ID
 */
const { fetch, useHook } = createFetchAndHook<
  TerminalTransportsByTerminalIdInput,
  TerminalTransport
>({
  api: wsfTerminalsApiMeta,
  endpoint: terminalTransportsByTerminalIdMeta,
  getCacheStrategy: () =>
    // eslint-disable-next-line @typescript-eslint/no-var-requires
    require("./shared/terminalTransports.endpoints").terminalTransportsGroup
      .cacheStrategy,
});

/**
 * Fetch function for retrieving transportation information for a specific terminal by ID
 */
export const fetchTerminalTransportsByTerminalId: FetchFactory<
  TerminalTransportsByTerminalIdInput,
  TerminalTransport
> = fetch;

/**
 * React Query hook for retrieving transportation information for a specific terminal by ID
 */
export const useTerminalTransportsByTerminalId: HookFactory<
  TerminalTransportsByTerminalIdInput,
  TerminalTransport
> = useHook;
